import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Trash2, RotateCcw, Loader2, Check } from 'lucide-react';
import { TaskLinkPicker } from '@/components/TaskLinkPicker';

const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 rounded-xl";
const lbl = "text-white/50 text-xs uppercase tracking-wider mb-1.5 block";
const FIELDS = ['title', 'description', 'due_date', 'due_time', 'assigned_to', 'assigned_to_name', 'lead_id', 'lead_name', 'deal_id', 'deal_name', 'status'];
const pick = f => Object.fromEntries(FIELDS.map(k => [k, f[k] ?? '']));

export const TaskEditDialog = ({ task, users, isAdmin, onClose, onSaved }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saveState, setSaveState] = useState('idle');
  const dirty = useRef(false);
  const timer = useRef(null);
  const formRef = useRef(null);
  formRef.current = form;

  useEffect(() => { setForm(task ? { ...task } : null); dirty.current = false; setSaveState('idle'); }, [task]);

  const persist = async (f = formRef.current) => {
    clearTimeout(timer.current);
    if (!f || !dirty.current || !f.title?.trim()) return;
    dirty.current = false;
    setSaveState('saving');
    try { await api.put(`/tasks/${f.id}`, pick(f)); setSaveState('saved'); onSaved(); }
    catch { setSaveState('idle'); toast.error('No se pudo guardar la tarea'); }
  };

  const set = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(), 700);
  };

  const close = async () => { await persist(); onClose(); };

  if (!form) return null;

  const toggle = async () => {
    const status = form.status === 'completada' ? 'pendiente' : 'completada';
    dirty.current = true;
    await persist({ ...form, status });
    toast.success(status === 'completada' ? 'Tarea completada' : 'Tarea reabierta');
    onClose();
  };
  const remove = async () => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    clearTimeout(timer.current); dirty.current = false;
    try { await api.del(`/tasks/${form.id}`); toast.success('Tarea eliminada'); onSaved(); onClose(); }
    catch { toast.error('No se pudo eliminar'); }
  };
  const openLink = path => { close(); navigate(path); };
  const done = form.status === 'completada';

  return (
    <Dialog open={!!task} onOpenChange={o => !o && close()}>
      <DialogContent data-testid="task-edit-dialog" onOpenAutoFocus={e => e.preventDefault()} className="bg-[#0A1F1A] border-[#1A3B32] text-white w-[calc(100%-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader className="flex-row items-center justify-between space-y-0 pr-6">
          <DialogTitle className="font-outfit text-lg">Editar tarea</DialogTitle>
          <span data-testid="task-edit-save-state" className="text-[11px] text-white/40 flex items-center gap-1">
            {saveState === 'saving' && <><Loader2 size={12} className="animate-spin" />Guardando…</>}
            {saveState === 'saved' && <><Check size={12} className="text-[#6BCB77]" />Guardado</>}
          </span>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className={lbl}>Nombre de la tarea *</Label>
            <Input data-testid="task-edit-title" value={form.title || ''} onChange={e => set({ title: e.target.value })} className={inputCls} />
            {!form.title?.trim() && <p className="text-[11px] text-red-400 mt-1">El nombre es obligatorio para guardar</p>}
          </div>
          <div>
            <Label className={lbl}>Descripción</Label>
            <Textarea data-testid="task-edit-description" value={form.description || ''} onChange={e => set({ description: e.target.value })}
              className="bg-[#0A1F1A] border-[#1A3B32] text-white focus:border-[#A38560] min-h-[80px] rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="min-w-0">
              <Label className={lbl}>Fecha</Label>
              <Input data-testid="task-edit-date" type="date" value={form.due_date || ''} onChange={e => set({ due_date: e.target.value })} className={`${inputCls} w-full min-w-0 [color-scheme:dark]`} />
            </div>
            <div className="min-w-0">
              <Label className={lbl}>Hora</Label>
              <Input data-testid="task-edit-time" type="time" value={form.due_time || ''} onChange={e => set({ due_time: e.target.value })} className={`${inputCls} w-full min-w-0 [color-scheme:dark]`} />
            </div>
          </div>
          {isAdmin && (
            <div>
              <Label className={lbl}>Encargado</Label>
              <Select value={form.assigned_to || ''} onValueChange={v => set({ assigned_to: v, assigned_to_name: users.find(u => u.id === v)?.name || '' })}>
                <SelectTrigger data-testid="task-edit-assignee" className={inputCls}><SelectValue placeholder="Seleccionar persona" /></SelectTrigger>
                <SelectContent className="bg-[#0A1F1A] border-white/10 text-white">{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <TaskLinkPicker form={form} onChange={set} onOpen={openLink} />
          {form.created_by_name && <p className="text-[11px] text-white/40">Creada por {form.created_by_name} · los cambios se guardan automáticamente</p>}
          <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2 pt-2">
            <button onClick={remove} data-testid="task-edit-delete" className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm text-red-400 hover:bg-red-400/10">
              <Trash2 size={14} />Eliminar
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={toggle} data-testid="task-edit-toggle"
                className={`flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium border ${done ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-[#6BCB77]/40 text-[#6BCB77] hover:bg-[#6BCB77]/10'}`}>
                {done ? <><RotateCcw size={14} />Reabrir</> : <><CheckCircle2 size={14} />Marcar completada</>}
              </button>
              <button onClick={close} data-testid="task-edit-close"
                className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D]">Listo</button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
